import React, { useEffect, useMemo, useState } from "react";
import { getAllReports } from "../../apis/orderApi";
import ExcelJS from "exceljs";
import Invoice from "../Invoice";
import StudentInvoices from "../StudentsInvoice";
import { customDate } from "../../utils/dateFormat";
import { Building2, Calendar, FileSpreadsheet, FileText, Layers, Package } from "lucide-react";
import toast from "react-hot-toast";

export const calculateAcademicYear = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "";
  const year = d.getFullYear();
  const month = d.getMonth(); // 0-11, April is 3
  if (month >= 3) {
    return `${year}-${year + 1}`;
  } else {
    return `${year - 1}-${year}`;
  }
};

const Report = () => {
  const [orders, setOrders] = useState([]);
  const [activeTab, setActiveTab] = useState("project"); // "project" | "date"
  
  // Filter States
  const [selectedInstitution, setSelectedInstitution] = useState("all");
  const [selectedAcademicYear, setSelectedAcademicYear] = useState("all");
  const [selectedOrder, setSelectedOrder] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [enableDateFilterInProject, setEnableDateFilterInProject] = useState(false);

  // Modals
  const [genInvoice, setGenInvoice] = useState(false);
  const [getAllInvoices, setGetAllInvoices] = useState(false);
  const [singleOrderForSlip, setSingleOrderForSlip] = useState(null);

  useEffect(() => {
    getAllReports()
      .then((res) => setOrders(res.data || []))
      .catch((err) => console.error("Error fetching orders:", err));
  }, []);

  // Unique Institutions / Projects
  const uniqueInstitutions = useMemo(() => {
    const map = new Map();
    orders.forEach((o) => {
      if (o.industry?._id) {
        map.set(o.industry._id, o.industry.name);
      }
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [orders]);

  // Unique Academic Years
  const uniqueAcademicYears = useMemo(() => {
    const years = new Set();
    orders.forEach((o) => {
      const ay = calculateAcademicYear(o.createdAt);
      if (ay) years.add(ay);
    });
    return Array.from(years).sort().reverse();
  }, [orders]);

  // Orders filtered by active selection
  const matchingOrders = useMemo(() => {
    let list = orders;

    if (activeTab === "project") {
      if (selectedInstitution && selectedInstitution !== "all") {
        list = list.filter((o) => o.industry?._id === selectedInstitution);
      }
      if (selectedAcademicYear && selectedAcademicYear !== "all") {
        list = list.filter(
          (o) => calculateAcademicYear(o.createdAt) === selectedAcademicYear
        );
      }
      if (enableDateFilterInProject && startDate && endDate) {
        const start = new Date(startDate);
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);

        list = list.filter((orderItem) =>
          orderItem.students?.some((student) => {
            if (!student.paymentDetails?.date) return false;
            const paymentDate = new Date(student.paymentDetails.date);
            return paymentDate >= start && paymentDate <= end;
          })
        );
      }
    } else {
      // "date" tab
      if (startDate && endDate) {
        const start = new Date(startDate);
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);

        list = list.filter((orderItem) =>
          orderItem.students?.some((student) => {
            if (!student.paymentDetails?.date) return false;
            const paymentDate = new Date(student.paymentDetails.date);
            return paymentDate >= start && paymentDate <= end;
          })
        );
      }
    }

    return list;
  }, [
    orders,
    activeTab,
    selectedInstitution,
    selectedAcademicYear,
    startDate,
    endDate,
    enableDateFilterInProject,
  ]);

  // Summary counts
  const totalMatchingStudents = useMemo(() => {
    const relevant =
      selectedOrder && selectedOrder !== "all"
        ? matchingOrders.filter((o) => o._id === selectedOrder)
        : matchingOrders;
    return relevant.reduce((acc, cur) => acc + (cur.students?.length || 0), 0);
  }, [matchingOrders, selectedOrder]);

  const handleDownload = async () => {
    let ordersToProcess = matchingOrders;
    if (selectedOrder && selectedOrder !== "all") {
      ordersToProcess = matchingOrders.filter((o) => o._id === selectedOrder);
    }

    if (!ordersToProcess || ordersToProcess.length === 0) {
      toast.error("No orders found matching the selected criteria.");
      return;
    }

    const hasDateFilter =
      (activeTab === "date" && startDate && endDate) ||
      (activeTab === "project" && enableDateFilterInProject && startDate && endDate);

    const start = startDate ? new Date(startDate) : null;
    const end = endDate ? new Date(endDate) : null;
    if (end) end.setHours(23, 59, 59, 999);

    const workbook = new ExcelJS.Workbook();

    /* ================= STUDENT / INVOICE SHEET ================= */
    const studentsSheet = workbook.addWorksheet("Invoice Report");
    studentsSheet.columns = [
      { header: "Roll No", key: "rollNo", width: 15 },
      { header: "Name", key: "name", width: 20 },
      { header: "Class", key: "class", width: 10 },
      { header: "Division", key: "division", width: 12 },
      { header: "Gender", key: "gender", width: 10 },
      { header: "House", key: "house", width: 15 },
      { header: "Phone", key: "phone", width: 15 },
      { header: "School / Project", key: "school", width: 28 },
      { header: "Branch", key: "branch", width: 18 },
      { header: "Academic Year", key: "academicYear", width: 16 },
      { header: "Order No", key: "orderNo", width: 18 },
      { header: "Invoice No", key: "invoiceNo", width: 18 },
      { header: "Tailor", key: "tailor", width: 18 },
      { header: "Ordered By", key: "orderBy", width: 18 },
      { header: "Products", key: "products", width: 40 },
      { header: "Cash Paid", key: "cash", width: 12 },
      { header: "GPay Paid", key: "gpay", width: 12 },
      { header: "Balance Paid", key: "balance", width: 14 },
      { header: "Unpaid Amount", key: "unpaid", width: 14 },
      { header: "Total Price", key: "totalPrice", width: 14 },
      { header: "Invoice Date", key: "invoiceDate", width: 22 },
      { header: "Created At", key: "createdAt", width: 22 },
      { header: "Payment Proof", key: "paymentLinks", width: 20 },
    ];

    /* ================= PRODUCT WORK SHEET ================= */
    const productsSheet = workbook.addWorksheet("Product Work");
    productsSheet.columns = [
      { header: "Product", key: "product", width: 30 },
      { header: "Quantity", key: "quantity", width: 10 },
      { header: "Measurement", key: "measurement", width: 25 },
      { header: "Description", key: "description", width: 40 },
      { header: "Created At", key: "createdAt", width: 22 },
    ];

    /* ================= SUMMARY SHEET ================= */
    const summarySheet = workbook.addWorksheet("Summary");
    summarySheet.columns = [
      { header: "Payment Mode", key: "category", width: 30 },
      { header: "Total Amount", key: "total", width: 20 },
    ];

    let totalCash = 0;
    let totalOnline = 0;
    let totalBalance = 0;
    let totalUnpaid = 0;
    let overallTotal = 0;

    const productMaps = [];

    ordersToProcess.forEach((currOrder) => {
      const orderAcademicYear = calculateAcademicYear(currOrder.createdAt);

      currOrder.students?.forEach((student) => {
        student.invoices?.forEach((inv) => {
          let invoiceDate = null;
          if (inv?.paymentDetails?.date) {
            invoiceDate = new Date(inv.paymentDetails.date);
          }

          if (hasDateFilter) {
            if (!invoiceDate || isNaN(invoiceDate.getTime())) return;
            if (invoiceDate < start || invoiceDate > end) return;
          }

          const payments = inv.paymentDetails?.payments || [];

          const cash = payments
            .filter((p) => p.method === "cash")
            .reduce((t, p) => t + p.amount, 0);

          const gpay = payments
            .filter((p) => p.method === "gpay")
            .reduce((t, p) => t + p.amount, 0);

          const balance = payments
            .filter((p) => p.method === "balance")
            .reduce((t, p) => t + p.amount, 0);

          const unpaid = inv.paymentDetails?.balance || 0;
          const totalPrice = inv.paymentDetails?.totalPrice || 0;

          const row = studentsSheet.addRow({
            rollNo: student.rollNo,
            name: student.name,
            class: student.class,
            division: student.division,
            gender: student.gender,
            house: student.house,
            phone: student.phone,
            school: currOrder.industry?.name || "-",
            branch: currOrder.branch?.name || "-",
            academicYear: orderAcademicYear || "-",

            orderNo: inv.orderNo || "-",
            invoiceNo: inv.invoiceNo || "-",
            tailor: String(inv.takenBy || "-").toUpperCase(),
            orderBy: inv.tailor || "-",

            products: inv.products
              ?.map((p) => {
                const name = p.product?.name || "Custom Product";
                const price = p.product?.price || p.price;
                return `${name}(qty-${p.quantity})(₹${price})`;
              })
              .join(",\n"),

            cash,
            gpay,
            balance,
            unpaid,
            totalPrice,

            invoiceDate: invoiceDate
              ? invoiceDate.toLocaleString()
              : customDate(inv.createdAt),
            createdAt: new Date(inv.createdAt).toLocaleString(),
          });

          /* Payment Proof */
          const proof = payments.map((p) => p.proffOfPayment).filter(Boolean)[0];
          if (proof) {
            const cell = row.getCell("paymentLinks");
            cell.value = { text: "View Proof", hyperlink: proof };
            cell.font = { color: { argb: "FF0000FF" }, underline: true };
          }

          totalCash += cash;
          totalOnline += gpay;
          totalBalance += balance;
          totalUnpaid += unpaid;
          overallTotal += totalPrice;

          /* Product Work */
          inv.products?.forEach((prod) => {
            const name = prod.product?.name || "Custom Product";
            const existing = productMaps.find((p) => p.product === name);

            if (existing) {
              existing.quantity += prod.quantity;
            } else {
              productMaps.push({
                product: name,
                quantity: prod.quantity,
                measurement: prod.measurement || "-",
                description: prod.description || "-",
                createdAt: invoiceDate
                  ? invoiceDate.toLocaleString()
                  : customDate(inv.createdAt),
              });
            }
          });
        });
      });
    });

    /* ================= PRODUCT SHEET ================= */
    productMaps.sort((a, b) => a.product.localeCompare(b.product));
    productMaps.forEach((p) => productsSheet.addRow(p));

    /* ================= SUMMARY ================= */
    summarySheet.addRow({ category: "Total Cash", total: totalCash });
    summarySheet.addRow({ category: "Total GPay", total: totalOnline });
    summarySheet.addRow({ category: "Total Balance Paid", total: totalBalance });
    summarySheet.addRow({ category: "Total Unpaid", total: totalUnpaid });
    summarySheet.addRow({ category: "Overall Total", total: overallTotal });

    /* ================= FORMATTING ================= */
    [studentsSheet, productsSheet, summarySheet].forEach((sheet) => {
      sheet.getRow(1).font = { bold: true };
      sheet.columns.forEach((col) => {
        col.alignment = { vertical: "middle", horizontal: "left", wrapText: true };
      });
    });

    /* ================= DOWNLOAD ================= */
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    let filenamePrefix = "Orders_Report";
    if (selectedInstitution && selectedInstitution !== "all") {
      const instName = orders.find((o) => o.industry?._id === selectedInstitution)?.industry?.name || "Project";
      filenamePrefix = `${instName.replace(/[^a-zA-Z0-9]/g, "_")}`;
    }
    if (selectedAcademicYear && selectedAcademicYear !== "all") {
      filenamePrefix += `_${selectedAcademicYear}`;
    }

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${filenamePrefix}_${Date.now()}.xlsx`;
    link.click();
    toast.success("Report downloaded successfully");
  };

  const handleOpenPackSlips = () => {
    const target =
      selectedOrder && selectedOrder !== "all"
        ? orders.find((o) => o._id === selectedOrder)
        : matchingOrders[0];

    if (!target) {
      toast.error("Please select an order to generate packing slips.");
      return;
    }
    setSingleOrderForSlip(target);
    setGetAllInvoices(true);
  };

  const handleOpenInvoice = () => {
    const target =
      selectedOrder && selectedOrder !== "all"
        ? orders.find((o) => o._id === selectedOrder)
        : matchingOrders[0];

    if (!target) {
      toast.error("Please select an order to generate invoice.");
      return;
    }
    setSingleOrderForSlip(target);
    setGenInvoice(true);
  };

  return (
    <div className="section mx-auto mt-8 max-w-2xl space-y-6">
      <div className="bg-white p-6 rounded-xl shadow-md border border-gray-100">
        <div className="flex items-center justify-between border-b pb-4 mb-5">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Generate Reports</h1>
            <p className="text-xs text-gray-500 mt-1">
              Download order reports by Project, Academic Year, or Custom Date range
            </p>
          </div>
          <FileSpreadsheet size={32} className="text-primary opacity-80" />
        </div>

        {/* Tab Selection */}
        <div className="flex rounded-lg bg-gray-200 p-1 mb-6">
          <button
            onClick={() => {
              setActiveTab("project");
              setSelectedOrder("all");
            }}
            className={`flex-1 py-2 text-sm font-semibold rounded-md transition flex items-center justify-center gap-2 ${
              activeTab === "project"
                ? "bg-white text-primary shadow"
                : "text-gray-700 hover:text-black"
            }`}
          >
            <Building2 size={16} />
            <span>By Project & Academic Year</span>
          </button>
          <button
            onClick={() => {
              setActiveTab("date");
              setSelectedOrder("all");
            }}
            className={`flex-1 py-2 text-sm font-semibold rounded-md transition flex items-center justify-center gap-2 ${
              activeTab === "date"
                ? "bg-white text-primary shadow"
                : "text-gray-700 hover:text-black"
            }`}
          >
            <Calendar size={16} />
            <span>By Date Range</span>
          </button>
        </div>

        <div className="space-y-4">
          {/* TAB 1: BY PROJECT & ACADEMIC YEAR */}
          {activeTab === "project" && (
            <>
              {/* Project / Institution Dropdown */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                  <Building2 size={14} className="text-primary" />
                  Select Project / Institution
                </label>
                <select
                  value={selectedInstitution}
                  onChange={(e) => {
                    setSelectedInstitution(e.target.value);
                    setSelectedOrder("all");
                  }}
                  className="w-full border border-gray-300 rounded-lg py-2.5 px-3.5 text-sm bg-gray-50 focus:bg-white focus:ring-2 focus:ring-primary focus:outline-none transition"
                >
                  <option value="all">All Projects / Institutions</option>
                  {uniqueInstitutions.map((inst) => (
                    <option key={inst.id} value={inst.id}>
                      {inst.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Academic Year Dropdown */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                  <Calendar size={14} className="text-primary" />
                  Select Academic Year
                </label>
                <select
                  value={selectedAcademicYear}
                  onChange={(e) => {
                    setSelectedAcademicYear(e.target.value);
                    setSelectedOrder("all");
                  }}
                  className="w-full border border-gray-300 rounded-lg py-2.5 px-3.5 text-sm bg-gray-50 focus:bg-white focus:ring-2 focus:ring-primary focus:outline-none transition"
                >
                  <option value="all">All Academic Years</option>
                  {uniqueAcademicYears.map((ay) => (
                    <option key={ay} value={ay}>
                      Academic Year: {ay}
                    </option>
                  ))}
                </select>
              </div>

              {/* Specific Order / Branch Dropdown */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                  <Layers size={14} className="text-primary" />
                  Select Order / Branch
                </label>
                <select
                  value={selectedOrder}
                  onChange={(e) => setSelectedOrder(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg py-2.5 px-3.5 text-sm bg-gray-50 focus:bg-white focus:ring-2 focus:ring-primary focus:outline-none transition"
                >
                  <option value="all">
                    All Orders in selection ({matchingOrders.length} order{matchingOrders.length !== 1 ? "s" : ""})
                  </option>
                  {matchingOrders.map((o) => (
                    <option key={o._id} value={o._id}>
                      {o.branch?.name || "Main Branch"} — {o.industry?.name} ({customDate(o.createdAt)})
                    </option>
                  ))}
                </select>
              </div>

              {/* Optional Date Range Toggle */}
              <div className="pt-2">
                <label className="inline-flex items-center gap-2 cursor-pointer text-xs font-semibold text-gray-700">
                  <input
                    type="checkbox"
                    checked={enableDateFilterInProject}
                    onChange={(e) => setEnableDateFilterInProject(e.target.checked)}
                    className="rounded text-primary focus:ring-primary h-4 w-4"
                  />
                  <span>Filter by specific payment date range within this project (Optional)</span>
                </label>

                {enableDateFilterInProject && (
                  <div className="grid grid-cols-2 gap-3 mt-3 p-3 bg-gray-50 rounded-lg border">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1 font-medium">
                        Start Date
                      </label>
                      <input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        className="border rounded-md w-full py-1.5 px-3 text-sm bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1 font-medium">
                        End Date
                      </label>
                      <input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        className="border rounded-md w-full py-1.5 px-3 text-sm bg-white"
                      />
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {/* TAB 2: BY DATE RANGE */}
          {activeTab === "date" && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="border border-gray-300 rounded-lg w-full py-2.5 px-3.5 text-sm bg-gray-50 focus:bg-white focus:ring-2 focus:ring-primary focus:outline-none transition"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="border border-gray-300 rounded-lg w-full py-2.5 px-3.5 text-sm bg-gray-50 focus:bg-white focus:ring-2 focus:ring-primary focus:outline-none transition"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                  <Layers size={14} className="text-primary" />
                  Select Order in Date Range
                </label>
                <select
                  value={selectedOrder}
                  onChange={(e) => setSelectedOrder(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg py-2.5 px-3.5 text-sm bg-gray-50 focus:bg-white focus:ring-2 focus:ring-primary focus:outline-none transition"
                >
                  <option value="all">
                    All Orders in date range ({matchingOrders.length} order{matchingOrders.length !== 1 ? "s" : ""})
                  </option>
                  {matchingOrders.map((o) => (
                    <option key={o._id} value={o._id}>
                      {o.branch?.name || "Main Branch"} — {o.industry?.name}
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}

          {/* Quick Summary Pill */}
          <div className="bg-gray-100 border border-gray-300 rounded-lg p-3 text-xs flex justify-between items-center text-gray-800">
            <div>
              <span className="font-semibold">Matching Orders:</span>{" "}
              {selectedOrder !== "all" ? 1 : matchingOrders.length}
            </div>
            <div>
              <span className="font-semibold">Total Students:</span>{" "}
              {totalMatchingStudents}
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col gap-2.5 pt-2">
            <button
              onClick={handleDownload}
              className="w-full bg-primary hover:opacity-90 text-white font-medium py-2.5 px-4 rounded-lg shadow transition flex items-center justify-center gap-2 cursor-pointer"
            >
              <FileSpreadsheet size={18} />
              <span>Download Excel Report</span>
            </button>

            <div className="grid grid-cols-2 gap-2.5">
              <button
                onClick={handleOpenPackSlips}
                className="w-full bg-blue-600 hover:opacity-90 text-white font-medium py-2 px-3 text-sm rounded-lg shadow transition flex items-center justify-center gap-1.5 cursor-pointer"
              >
                <Package size={16} />
                <span>Packing Slips</span>
              </button>

              <button
                onClick={handleOpenInvoice}
                className="w-full bg-green-600 hover:opacity-90 text-white font-medium py-2 px-3 text-sm rounded-lg shadow transition flex items-center justify-center gap-1.5 cursor-pointer"
              >
                <FileText size={16} />
                <span>Invoice Summary</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Invoice Modal */}
      {genInvoice && singleOrderForSlip && (
        <div className="fixed inset-0 w-full h-full bg-black bg-opacity-50 z-50">
          <div className="w-full mx-auto h-full overflow-y-auto">
            <Invoice
              selectedOrder={singleOrderForSlip}
              setGenInvoice={setGenInvoice}
            />
          </div>
        </div>
      )}

      {/* Pack Slips Modal */}
      {getAllInvoices && singleOrderForSlip && (
        <div className="fixed inset-0 w-full h-full bg-black bg-opacity-50 z-50">
          <div className="w-full mx-auto h-full overflow-y-auto">
            <StudentInvoices
              order={singleOrderForSlip}
              students={singleOrderForSlip.students || []}
              setShowInvoice={setGetAllInvoices}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Report;
